import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, FormGroup, PageHeader, Textarea } from "@cofano/cck";
import Helmet from "components/helmet/Helmet";
import { intl } from "LocaleWrapper";
import { useApolloClient, useMutation } from "@apollo/client";
import useGroupedTable from "components/customCompenents/table/useGroupedTable";
import { SalesCharterStatus } from "__generated__/graphql";
import GroupedTable from "components/customCompenents/table/GroupedTable";
import { DossierLink, ProductLink, RelationLink, ShipLink } from "utils/LinkUtils";
import SalesCharterStatusChanger from "view/charters/components/status/SalesCharterStatusChanger";
import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";
import { locationDescriptionWithGroup } from "view/locations/utils/LocationUtils";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { CustomDialog } from "components/dialogs/Dialogs";
import CustomerStatusChanger from "view/customerUpdate/components/status/CustomerStatusChanger";
import { Cargo } from "view/cargos/types/cargo";
import { Relation } from "view/relations/types/relation";
import { SalesCharter } from "view/charters/types/charter";
import { ContactRoleType } from "__generated__/enums";
import CustomerUpdateStatusChanger from "view/customerUpdate/components/status/CustomerUpdateStatusChanger";
import { useCustomerUpdateState } from "./CustomerUpdateContext";
import { InfoOutlined, InsertDriveFile, WarningOutlined } from "@mui/icons-material";
import "./styling/styling.scss";
import { formatDate, FormattedDate } from "utils/DateUtils";
import CustomerUpdateCommentDialog from "view/customerUpdate/components/dialogs/CustomerUpdateCommentDialog.tsx";
import CustomerUpdateShipLocationDialog
	from "view/customerUpdate/components/dialogs/CustomerUpdateShipLocationDialog.tsx";
import CustomerUpdateShipLocationChanger from "view/customerUpdate/components/text/CustomerUpdateShipLocationChanger";
import { Ship } from "types/index";
import CustomerUpdateEstimateTimeDialog from "view/customerUpdate/components/dialogs/CustomerUpdateEstimateTimeDialog";
import CustomerUpdatePdfDrawer from "view/customerUpdate/components/pdf/CustomerUpdatePdfDrawer";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import {
	SendCustomerUpdateDocument,
	SendCustomerUpdateMutation,
} from "__generated__/view/customerUpdate/graphql/mutations.generated";
import { SalesChartersDocument } from "__generated__/view/customerUpdate/graphql/queries.generated";
import { useConfirm } from "components/hooks/useConfirm";
import { StatusEstimateType } from "__generated__/enums";
import ConnectingVisitField from "view/cargos/components/connectingVisits/ConnectingVisitField";
import CargoActionQuantity from "view/cargos/components/quantities/CargoActionQuantity";
import CargoQuantity from "view/cargos/components/quantities/CargoQuantity";
import { CustomerUpdateStatusChangeFragment } from "__generated__/view/customerUpdate/graphql/fragments.generated";

export type ShipType = Pick<Ship, "id" | "name" | "position" | "nextCall">;

type CustomerOrBrokerProps = {
	relation: Relation;
	charters: SalesCharter[];
	isUpToDate: boolean;
	lastSentOn?: number | null;
	loading: boolean;
	onLoadCustomerUpdates: (relationIds: number[], force?: boolean) => Promise<void>;
	onRefresh: () => void;
}

const getLastCommunicatedToCustomerStatus = (cargo: Cargo) => {
	const statusChanges = cargo.statusChanges?.filter((x): x is CustomerUpdateStatusChangeFragment & { communicatedToCustomer: number } => x.communicatedToCustomer != null) ?? [];

	if (statusChanges.length === 0) {
		return undefined;
	}

	return statusChanges.reduce((latest, current) =>
		current.communicatedToCustomer > latest.communicatedToCustomer
			? current
			: latest,
	);
};

const getLoadingTs = (action?: { start?: number | null }) => {
	if (!action) {
		return Number.MAX_SAFE_INTEGER;
	}

	return action.start ?? Number.MAX_SAFE_INTEGER;
};

const LiveDiffIndicator = ({ tooltip }: { tooltip: string }) => (
	<Tooltip title={tooltip}>
		<InfoOutlined
			color="primary"
			fontSize="small"
			sx={{ verticalAlign: "middle", flexShrink: 0 }}
		/>
	</Tooltip>
);

const CustomerUpdateOverview = () => {
	const client = useApolloClient();
	const state = useCustomerUpdateState();
	const stateRef = useRef(state);
	stateRef.current = state;
	const confirm = useConfirm();

	const [ charterGroups, setCharterGroups ] = useState<Record<number | string, { relation: Relation; charters: SalesCharter[] }>>({});

	// Status Comment
	const [ commentDialogOpen, setCommentDialogOpen ] = useState(false);
	const [ comment, setComment ] = useState<string | undefined>(undefined);
	const [ commentTarget, setCommentTarget ] = useState<{ relationId: number; cargoId: number } | undefined>(undefined);

	// Ship Location
	const [ shipLocationDialogOpen, setShipLocationDialogOpen ] = useState(false);
	const [ selectedShip, setSelectedShip ] = useState<ShipType | undefined>(undefined);
	const [ selectedRelation, setSelectedRelation ] = useState<Relation | undefined>(undefined);

	// Estimate time
	const [ estimateTimeDialogOpen, setEstimateTimeDialogOpen ] = useState(false);
	const [ estimateTime, setEstimateTime ] = useState<number | undefined>(undefined);
	const [ estimateTimeTarget, setEstimateTimeTarget ] = useState<{ relationId: number; cargoId: number; statusChangeId: number; } | undefined>(undefined);

	const saveCargo = (relationId: number, cargoId: number, update: Partial<Parameters<typeof state.saveCargo>[1]>) => {
		void stateRef.current.saveCargo(relationId, {
			cargoId,
			...update,
		});
	};

	const openCommentDialog = (relationId: number, cargoId: number, defaultValue: string) => {
		setCommentTarget({ relationId, cargoId });
		setComment(defaultValue);
		setCommentDialogOpen(true);
	};

	const closeCommentDialog = () => {
		setCommentTarget(undefined);
		setComment(undefined);
		setCommentDialogOpen(false);
	};

	const onCommentUpdate = async(value?: string) => {
		if (commentTarget == null) {
			return;
		}

		const { relationId, cargoId } = commentTarget;

		saveCargo(relationId, cargoId, {
			comment: value || null,
		});

		closeCommentDialog();
	};

	const onShipLocationChange = (ship: ShipType, relation: Relation) => {
		setSelectedShip(ship);
		setSelectedRelation(relation);
		setShipLocationDialogOpen(true);
	};

	const closeShipLocationDialog = () => {
		setSelectedShip(undefined);
		setSelectedRelation(undefined);
		setShipLocationDialogOpen(false);
	};

	const openEstimateTimeDialog = (relationId: number, cargo: Cargo) => {
		const selectedStatus = getSelectedStatus(relationId, cargo);
		if (selectedStatus?.id == null) {
			return;
		}

		setEstimateTimeTarget({
			relationId,
			cargoId: cargo.id,
			statusChangeId: selectedStatus.id,
		});

		const existing = stateRef.current.getCargo(relationId, cargo.id);
		const defaultValue = existing?.estimateTime ?? selectedStatus.callEstimateTime ?? undefined;

		setEstimateTime(defaultValue);
		setEstimateTimeDialogOpen(true);
	};

	const closeEstimateTimeDialog = () => {
		setEstimateTimeDialogOpen(false);
		setEstimateTimeTarget(undefined);
		setEstimateTime(undefined);
	};

	const onEstimateTimeUpdate = async() => {
		if (estimateTimeTarget == null) {
			return;
		}

		const { relationId, cargoId, statusChangeId } = estimateTimeTarget;

		saveCargo(relationId, cargoId, {
			statusChangeId,
			estimateTime,
		});

		closeEstimateTimeDialog();
	};

	const getSelectedStatus = (relationId: number, cargo: Cargo): CustomerUpdateStatusChangeFragment | undefined => {
		const snapshot = stateRef.current.getCargo(relationId, cargo.id);
		const fallback = getLastCommunicatedToCustomerStatus(cargo);

		const selectedId = snapshot?.statusChangeId ?? fallback?.id;

		return (selectedId != null ? cargo.statusChanges?.find(x => x.id === selectedId) : undefined) ?? fallback;
	};

	const getDisplayComment = (relationId: number, cargo: Cargo, selectedStatus?: CustomerUpdateStatusChangeFragment) => {
		const snapshot = stateRef.current.getCargo(relationId, cargo.id);
		return snapshot?.comment ?? selectedStatus?.comment ?? "";
	};

	const getDisplayEstimate = (relationId: number, cargo: Cargo, selectedStatus?: CustomerUpdateStatusChangeFragment) => {
		const snapshot = stateRef.current.getCargo(relationId, cargo.id);
		return snapshot?.estimateTime ?? selectedStatus?.callEstimateTime ?? null;
	};

	const createSyncInputs = () => {
		return Object.values(charterGroups).map(({ relation, charters }) => {
			const cargoIds = new Set<number>();
			const shipIds = new Set<number>();

			for (const charter of charters) {
				for (const cargo of (charter.cargos ?? [])) {
					cargoIds.add(cargo.id);
				}

				const ship = charter.voyages?.[0]?.ship;
				if (ship) {
					shipIds.add(ship.id);
				}
			}

			return {
				relationId: relation.id,
				cargoIds: Array.from(cargoIds),
				shipIds: Array.from(shipIds),
			};
		});
	};

	const onUpdateData = async() => {
		const inputs = createSyncInputs();
		await state.syncAllLatest({ inputs });
		await state.loadAllCustomerUpdates(relationIds, true);
		refetchByWebsocket();
	};

	const onRevertAll = () => {
		confirm({
			type: "WARNING",
			title: intl.formatMessage({ id: "CUSTOMER_UPDATE_REVERT_TITLE" }),
			content: <>{intl.formatMessage({ id: "CUSTOMER_UPDATE_REVERT_CONTENT" })}</>,
			confirmText: intl.formatMessage({ id: "CUSTOMER_UPDATE_REVERT_TEXT" }),
		}).then(async() => {
			await state.revertAllCustomerUpdates();
			await state.loadAllCustomerUpdates(relationIds, true);
			refetchByWebsocket();
		});
	};

	const notUpToDateRelations = useMemo(() => {
		return Object.values(charterGroups)
			.filter(({ relation }) => !state.isRelationUpToDate(relation.id))
			.map(({ relation }) => relation)
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [ charterGroups, state ]);

	const relationIds = useMemo(() => {
		return Object.values(charterGroups).map(x => Number(x.relation.id));
	}, [ charterGroups ]);


	const loadedUpdatesRef = useRef<string>("");
	useEffect(() => {
		if (relationIds.length === 0) {
			return;
		}

		const ref = relationIds.slice().sort((x, y) => x - y).join(",");
		if (ref === loadedUpdatesRef.current) {
			return;
		}

		loadedUpdatesRef.current = ref;
		void state.loadAllCustomerUpdates(relationIds);
	}, [ relationIds, state ]);


	const renderKeyRef = useRef(0);
	const lastStateRef = useRef(state);
	if (lastStateRef.current !== state) {
		lastStateRef.current = state;
		renderKeyRef.current += 1;
	}
	const renderKey = renderKeyRef.current;

	const { tableProps, refetchByWebsocket } = useGroupedTable({
		id: "customerUpdateOverview",
		paginationEnabled: false,
		headerInFront: true,
		size: "compact",
		spacingBetweenGroups: 5,
		onChange: async(pagination, filters) => {

			const getFilterValue = (property: string) => {
				return filters.find(it => it.property === property)?.value;
			};

			const result = await client.query({
				query: SalesChartersDocument,
				variables: {
					pagination: {
						...pagination,
						page: pagination.page,
						size: 500, // TODO: implement no pagination like ship planning
					},
					search: {
						id: getFilterValue("id") ? (Number.isInteger(getFilterValue("id")) ? getFilterValue("id") : 0) : undefined,
						statuses: [ SalesCharterStatus.Confirmed, SalesCharterStatus.Planned, SalesCharterStatus.InProgress ],
						excludeDeprecatedCustomers: true,
					},
				},
			});

			const groupedCharters = (result.data.salesCharters.content ?? []).reduce(
				(acc, charter) => {
					const relation = charter.broker ?? charter.debtor;
					const key = relation.id;

					acc[key] ??= {
						relation: relation as Relation,
						charters: [] as SalesCharter[],
					};

					// @ts-expect-error
					acc[key].charters.push(charter as SalesCharter);
					return acc;
				},
				{} as Record<number | string, { relation: Relation; charters: SalesCharter[] }>);

			setCharterGroups(groupedCharters);

			const sortedCharters = Object.values(groupedCharters).sort((a, b) => (a.relation.name).localeCompare(b.relation.name));

			return {
				groups: sortedCharters.map(({ relation, charters }) => ({
					id: relation.id,
					backgroundColor: "#D6E4F1",
					render: () => <CustomerOrBroker
						relation={relation}
						charters={charters}
						isUpToDate={stateRef.current.isRelationUpToDate(relation.id)}
						lastSentOn={stateRef.current.getSnapshot(relation.id)?.lastSentOn}
						loading={stateRef.current.isLoading(relation.id)}
						onLoadCustomerUpdates={stateRef.current.loadAllCustomerUpdates}
						onRefresh={refetchByWebsocket}
					/>,
					subGroups: charters
						.filter(c => c.cargos?.length ?? 0 > 0)
						.sort((a, b) => (a.firstLoadingDate ?? 0) - (b.firstLoadingDate ?? 0 ))
						.map(charter => ({
							id: charter.id,
							backgroundColor: "#eee",
							render: () => (
								<Box sx={{ display: "flex", alignItems: "center" }}>
									<div style={{ width: 120 }}>
										<DossierLink charter={charter} />
									</div>

									<div style={{ width: 180 }}>
										<SalesCharterStatusChanger
											/* @ts-expect-error */
											charter={charter}
											onCompleted={refetchByWebsocket} />
									</div>

									{charter.voyages?.map(voyage => (
										<ShipLink ship={voyage.ship} />
									))}

									{charter.voyages?.length == 0 && (
										<i style={{ color: "grey" }}>{intl.formatMessage({ id: "TO_BE_NOMINATED" })}</i>
									)}
								</Box>
							),
							rows: (charter.cargos ?? [])
								.slice()
								.sort((a, b) => {
									const ta = getLoadingTs(a?.firstLoadingAction);
									const tb = getLoadingTs(b?.firstLoadingAction);
									if (ta !== tb) {
										return ta - tb;
									}

									return (a.cargoNumber ?? "").localeCompare(b.cargoNumber ?? "");
								})
								.map(cargo => ({ ...cargo, communicationRelation: relation })),
						})),

				})),
				totalElements: result.data.salesCharters.totalElements,
			};
		},
	});

	const shipLocationForDialog = (() => {
		if (selectedRelation == null || selectedShip == null) {
			return undefined;
		}
		const shipSnapshot = state.getShip(selectedRelation.id, selectedShip.id);
		const lastCommunicated = selectedShip.position?.communicatedToCustomer != null ? selectedShip.position.location : undefined;
		return shipSnapshot?.location ?? lastCommunicated;
	})();

	return (
		<div className="customer-update">
			<PageHeader>
				<PageHeader.Title>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						{notUpToDateRelations.length > 0 && (
							<Tooltip placement="bottom"
								title={<Box>
									<Typography fontWeight={700} fontSize={13}>
										{intl.formatMessage({ id: "CUSTOMER_UPDATE_UNSENT_CHANGES" })}
										{notUpToDateRelations.map(x => (
											<Typography key={x.id} fontSize={12}>
												• {x.name}
											</Typography>
										))}
									</Typography>
								</Box>}>
								<WarningOutlined fontSize="small" />
							</Tooltip>
						)}
						{intl.formatMessage({ id: "CUSTOMER_UPDATE_OVERVIEW" })}
					</Box>
					<Helmet title={intl.formatMessage({ id: "CUSTOMER_UPDATE_OVERVIEW" })} />
				</PageHeader.Title>
				<PageHeader.Actions>
					<Button
						loading={state.isSyncLoading}
						onClick={onUpdateData}
						variant="outlined">
						{intl.formatMessage({ id: "UPDATE_DATA" })}
					</Button>
					<Button
						loading={state.isRevertLoading}
						variant="outlined"
						onClick={onRevertAll}>
						<SettingsBackupRestoreIcon />
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<GroupedTable
					{...tableProps}
					renderKey={renderKey}
					getEntityId={row => row.id}
					columns={[
						{
							id: "customerOrBroker",
							title: intl.formatMessage({ id: "CUSTOMER_OR_BROKER" }),
							width: 170,
						}, {
							id: "cargo",
							title: intl.formatMessage({ id: "CARGO" }),
							maxWidth: 150,
							render: (row) => <span style={{ display: "flex", gap: 12 }}>{row.cargoNumber} <ProductLink product={row.product} /></span>,
						}, {
							id: "weight",
							title: intl.formatMessage({ id: "QUANTITY" }),
							maxWidth: 150,
							render: (row) => {
								const hasActuals = row?.firstLoadingAction?.measurements?.locationWeight
									|| row?.firstLoadingAction?.measurements?.locationVolume
									|| row?.firstLoadingAction?.measurements?.skipperWeight
									|| row?.firstLoadingAction?.measurements?.skipperVolume;

								return <>
									{hasActuals && row.firstLoadingAction ? (
										<CargoActionQuantity
											cargoAction={row.firstLoadingAction}
											cargo={{
												...row,
												isWeightPrimary: row.isWeightPrimary ?? false,
												isVolumePrimary: row.isVolumePrimary ?? false,
												waterLevelOptimised: row.waterLevelOptimised ?? false,
											}}
											convertActualsToMton
											fallbackToActuals
										/>
									) : (
										<CargoQuantity
											cargo={{
												...row,
												isWeightPrimary: row.isWeightPrimary ?? false,
												isVolumePrimary: row.isVolumePrimary ?? false,
												waterLevelOptimised: row.waterLevelOptimised ?? false,
											}}
											grow
										/>
									)}
								</>;
							},
						}, {
							id: "loading",
							title: intl.formatMessage({ id: "LOADING" }),
							render: (row) => row.firstLoadingAction && (
								<div className="action-info">
									<CargoActionDate action={row.firstLoadingAction} withoutYear />
									<Tooltip title={locationDescriptionWithGroup(row.firstLoadingAction.location)}>
										<span>{locationDescriptionWithGroup(row.firstLoadingAction.location)}</span>
									</Tooltip>
									<ConnectingVisitField action={row.firstLoadingAction} />
								</div>
							),
						}, {
							id: "discharging",
							title: intl.formatMessage({ id: "DISCHARGING" }),
							render: (row) => row.lastDischargingAction && (
								<div className="action-info">
									{!row.lastDischargingAction.simulatedTimestamp ? <CargoActionDate action={row.lastDischargingAction} /> : <span></span>}
									<Tooltip title={locationDescriptionWithGroup(row.lastDischargingAction.location)}>
										<span>{locationDescriptionWithGroup(row.lastDischargingAction.location)}</span>
									</Tooltip>
									<ConnectingVisitField action={row.lastDischargingAction} />
								</div>
							),
						}, {
							id: "location",
							title: intl.formatMessage({ id: "LOCATION" }),
							render: (row) => {
								const relation = row.communicationRelation;
								const ship = row.charter?.voyages?.[0]?.ship;
								if (relation == null || ship == null) {
									return null;
								}

								const shipSnapshot = stateRef.current.getShip(relation.id, ship.id);
								const lastCommunicated = ship.position?.communicatedToCustomer != null ? ship.position?.location : undefined;
								const locationToDisplay = shipSnapshot?.location ?? lastCommunicated;
								const actualLocation = ship?.position?.location;
								const differs = actualLocation != null && actualLocation.trim() !== (locationToDisplay ?? "").trim();

								return (
									<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
										<CustomerUpdateShipLocationChanger
											ship={ship}
											location={locationToDisplay}
											onEdit={() => onShipLocationChange(ship, relation)} />
										{differs && <LiveDiffIndicator tooltip={`${intl.formatMessage({ id: "ACTUAL_LOCATION" })}: ${actualLocation}`} />}
									</Box>
								);
							},
						},
						{
							id: "status",
							title: intl.formatMessage({ id: "STATUS" }),
							render: (row) => {
								const relation = row.communicationRelation;
								if (relation == null) {
									return null;
								}

								const selectedStatus = getSelectedStatus(relation.id, row);
								const liveStatus = row.lastStatusChange;
								const differs = liveStatus != null && liveStatus.id !== selectedStatus?.id;

								return (
									<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
										<CustomerUpdateStatusChanger
											cargo={row}
											selectedStatusChange={selectedStatus}
											onStatusChange={(statusChange) => {
												saveCargo(relation.id, row.id, {
													statusChangeId: statusChange.id,
												});
											}}
										/>
										{differs && <LiveDiffIndicator tooltip={`${intl.formatMessage({ id: "ACTUAL_STATUS" })}: ${liveStatus.status?.translations?.defaultValue ?? liveStatus.status.description}`} />}
									</Box>
								);
							},
						},
						{
							id: "estimate",
							title: intl.formatMessage({ id: "ESTIMATE" }),
							maxWidth: 120,
							render: (row) => {
								const relation = row.communicationRelation;
								if (relation == null) {
									return null;
								}

								const selectedStatus = getSelectedStatus(relation.id, row);
								const estimateToDisplay = selectedStatus?.status?.estimateType != null
									? getDisplayEstimate(relation.id, row, selectedStatus)
									: null;

								const latestStatus = row.lastStatusChange as CustomerUpdateStatusChangeFragment;
								const liveEstimate = latestStatus?.callEstimateTime;
								const differs = (liveEstimate ?? null) !== (estimateToDisplay ?? null);

								if (estimateToDisplay == null && !differs) {
									return null;
								}

								return (
									<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 100 }}>
										{estimateToDisplay != null && (
											<Typography component="span"
												className="editable"
												fontSize={13}
												onClick={() => openEstimateTimeDialog(relation.id, row)}>
												<FormattedDate date={estimateToDisplay} withoutYear />
												{latestStatus?.status.estimateType && latestStatus?.status.estimateType != StatusEstimateType.StatusChange && (
													<sub><small>{intl.formatMessage({ id: latestStatus.status.estimateType.toString() })}</small></sub>
												)}
											</Typography>
										)}
										{differs && <LiveDiffIndicator tooltip={`${intl.formatMessage({ id: "ACTUAL_ESTIMATE" })}: ${liveEstimate ? formatDate(liveEstimate, undefined, true) : ""}`} />}
									</Box>
								);
							},
						},
						{
							id: "comment",
							title: intl.formatMessage({ id: "COMMENT" }),
							render: (row) => {
								const relation = row.communicationRelation;
								if (relation == null) {
									return null;
								}
								const selectedStatus = getSelectedStatus(relation.id, row);

								if (selectedStatus == null) {
									return null;
								}

								const commentToDisplay = getDisplayComment(relation.id, row, selectedStatus);
								const liveComment = row.lastStatusChange?.comment ?? "";
								const differs = commentToDisplay !== liveComment;

								return (
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexGrow: 1 }}>
										<span
											className="comment"
											style={{ flexGrow: 1 }}
											onClick={() => openCommentDialog(relation.id, row.id, commentToDisplay)}>
											{commentToDisplay ? commentToDisplay : <div className="comment-placeholder" />}
										</span>
										{differs && <LiveDiffIndicator tooltip={`${intl.formatMessage({ id: "ACTUAL_COMMENT" })}: ${liveComment || intl.formatMessage({ id: "NO_COMMENT" })}`} />}
									</Box>
								);
							},
						},
					]}
				/>
			</Card>

			{commentDialogOpen && (
				<CustomerUpdateCommentDialog
					onClose={closeCommentDialog}
					onUpdate={onCommentUpdate}
					comment={comment} />
			)}

			<CustomerUpdateEstimateTimeDialog
				open={estimateTimeDialogOpen}
				onClose={closeEstimateTimeDialog}
				onUpdate={onEstimateTimeUpdate}
				value={estimateTime}
				setValue={setEstimateTime} />

			{selectedShip && selectedRelation && (
				<CustomerUpdateShipLocationDialog
					open={shipLocationDialogOpen}
					onClose={closeShipLocationDialog}
					currentLocation={shipLocationForDialog ?? undefined}
					placeholder={selectedShip.position?.location || undefined}
					onSave={(location) => {
						void state.saveShip(selectedRelation.id, {
							shipId: selectedShip.id,
							location,
						});
					}} />
			)}
		</div>
	);
};

const CustomerOrBroker = ({ relation, charters, isUpToDate, lastSentOn, loading, onLoadCustomerUpdates, onRefresh }: CustomerOrBrokerProps) => {
	const [ emailNote, setEmailNote ] = useState("");
	const [ showPdfDrawer, setShowPdfDrawer ] = useState(false);
	const [ showSendModal, setShowSendModal ] = useState(false);

	const [ sendCustomerUpdate, { loading: sending } ] = useMutation<SendCustomerUpdateMutation>(SendCustomerUpdateDocument);

	const customerUpdateContacts = useMemo(() => {
		const seen = new Set<number>();
		return charters
			.flatMap(c => c.contacts ?? [])
			.filter(c => c.contactRoleType === ContactRoleType.CustomerUpdate && c.contact.email != null)
			.map(c => c.contact)
			.filter(c => {
				if (seen.has(c.id)) {
					return false;
				}
				seen.add(c.id);
				return true;
			});
	}, [ charters ]);

	const onSendCustomerUpdate = async() => {
		await sendCustomerUpdate({
			variables: {
				relationId: relation.id,
				emailNote: emailNote || undefined,
			},
		});

		await onLoadCustomerUpdates([ relation.id ], true);
		setShowSendModal(false);
		setEmailNote("");
		onRefresh();
	};

	return (
		<>
			<Box>
				<Box marginTop={1} marginBottom={1}>
					<Tooltip title={relation.name}>
						<b><RelationLink relation={relation} /></b>
					</Tooltip>
				</Box>

				<Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-start", justifyContent: "space-between" }}>
					{(loading || sending) ? <span>
						<CircularProgress size={14} />
					</span> : (
						<CustomerStatusChanger isUpToDate={isUpToDate} lastSentOn={lastSentOn} />
					)}

					<Tooltip title={intl.formatMessage({ id: "SEND" })}>
						<IconButton
							disabled={loading || sending}
							onClick={() => setShowPdfDrawer(true)}
							size="small"
							color="primary"
						>
							<InsertDriveFile fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<CustomDialog
				open={showSendModal}
				onClose={() => setShowSendModal(false)}
				title={intl.formatMessage({ id: "CUSTOMER_UPDATE_SEND_CONFIRM" })}
				maxWidth="sm"
				actions={(
					<Button
						loading={sending}
						onClick={onSendCustomerUpdate}
						disabled={customerUpdateContacts.length === 0 || sending}
						size="small">
						{intl.formatMessage({ id: "SEND" })}
					</Button>
				)}>
				<div style={{ marginBottom: 16 }}>
					{customerUpdateContacts.map(co => (
						<li key={co.id} style={{ marginLeft: 16 }}>
							{`${co.name}${co.email ? " (" + co.email + ")" : ""}`}
						</li>
					))}
				</div>

				<FormGroup label={intl.formatMessage({ id: "EMAIL_NOTE" })} tooltip={intl.formatMessage({ id: "EMAIL_NOTE_TOOLTIP" })}>
					<Textarea onChange={(e) => setEmailNote(e.target.value)} value={emailNote} />
				</FormGroup>
			</CustomDialog>

			<CustomerUpdatePdfDrawer
				relation={relation}
				open={showPdfDrawer}
				onClose={() => setShowPdfDrawer(false)}
				onSend={() => {
					setShowPdfDrawer(false);
					setShowSendModal(true);
				}}
			/>
		</>
	);
};

export default CustomerUpdateOverview;
